import { useMemo } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import {
  DrugTestingHero,
  DrugTestingPageShell,
  DrugTestPriceContact,
  useDrugTestingCopy,
} from "@/components/site/drug-testing/DrugTestingShared";
import {
  drugSampleFromPathSlug,
  drugTestingHref,
  privateDrugBookingUrl,
  privateDrugPackages,
} from "@/data/drugTesting";
import { useBookingConfirmationRedirect } from "@/hooks/useBookingConfirmationRedirect";
import { PageMeta } from "@/components/site/PageMeta";

const fill = (template: string, values: Record<string, string>) =>
  Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`{${key}}`, value), template);

const DrogtestningBoka = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const { lang, copy } = useDrugTestingCopy();
  const type = drugSampleFromPathSlug(slug);
  const purpose = searchParams.get("andamal") === "intyg" ? "intyg" : "privat";
  useBookingConfirmationRedirect({
    active: Boolean(type),
    slug: type === "saliv" ? "salivtest" : type === "urin" ? "urinprov" : null,
    mode: "onsite",
  });

  const pack = type ? privateDrugPackages[type] : null;
  const labelLower = pack
    ? pack.label[lang].toLocaleLowerCase(lang === "sv" ? "sv-SE" : "en")
    : "";

  const detailHref = useMemo(() => {
    if (!type) return drugTestingHref("hub", lang);
    return type === "saliv" ? drugTestingHref("salivtest", lang) : drugTestingHref("urinprov", lang);
  }, [lang, type]);


  if (!type || !pack) {
    return <Navigate to={drugTestingHref("hub", lang)} replace />;
  }

  const pageTitle =
    lang === "sv"
      ? `Boka ${pack.label.sv.toLowerCase()} – ${pack.price.sv} | Intygsläkaren`
      : `Book ${pack.label.en.toLowerCase()} – ${pack.price.en} | Intygsläkaren`;

  const b = copy.book;

  return (
    <DrugTestingPageShell
      breadcrumb={[
        { label: pack.label[lang], to: detailHref },
        { label: b.label },
      ]}
    >
      <PageMeta title={pageTitle} description={copy.book.subtitle} />
      <DrugTestingHero label={b.label} title={fill(b.title, { label: labelLower })}>
        <p>
          <strong className="text-foreground">{pack.price[lang]}</strong> · {b.subtitle}
        </p>
        <p>{fill(b.includes, { analytes: pack.analytes[lang].join(", ") })}</p>
      </DrugTestingHero>

      <section className="py-14 lg:py-16">
        <div className="container-wide max-w-4xl">
          <div className="overflow-hidden rounded-3xl border border-border bg-card p-6 shadow-card sm:p-8">
            <span className="text-sm font-semibold uppercase tracking-wider text-primary">
              {fill(b.calendarEyebrow, { label: pack.label[lang], price: pack.price[lang] })}
            </span>
            <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
              {b.calendarTitle}
            </h2>
            <p className="mt-3 text-base text-muted-foreground">
              {fill(b.calendarText, { label: labelLower, price: pack.price[lang] })}
            </p>
            {purpose === "intyg" ? (
              <p className="mt-3 text-base text-muted-foreground">{b.intygNote}</p>
            ) : null}
            <div className="mt-6 overflow-hidden rounded-2xl border border-border">
              <iframe
                src={privateDrugBookingUrl(type)}
                title={fill(b.iframeTitle, { label: labelLower })}
                className="h-[70vh] min-h-[520px] w-full border-0"
                loading="lazy"
                allow="payment; clipboard-write"
              />
            </div>
          </div>

          <div className="mt-8">
            <DrugTestPriceContact purpose={purpose} />
            <p className="mt-4">
              <Link to={detailHref} className="text-sm font-medium text-primary hover:text-primary/80">
                {fill(b.readMore, { label: labelLower })}
              </Link>
            </p>
          </div>
        </div>
      </section>
    </DrugTestingPageShell>
  );
};

export default DrogtestningBoka;
